import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";
import {
  OPTIONAL_NUMERIC_TEXT_INPUT,
  OPTIONAL_TEXT_INPUT,
} from "./optionalInputs";

export const supplierSendingSetupInputSchema = z.object({
  dataSourceLink: z.object({
    id: z.string().uuid(),
    dataSourceId: z.string().uuid().optional(),
  }),
  metadataId: z.string().min(1),
  changeOperation: z.enum(["insert", "update", "delete"]),
  changeLsn: z.string().regex(/^\d+$/),
  vendorCode: OPTIONAL_NUMERIC_TEXT_INPUT.describe(
    "The vendor's code in the source. Its trailing number, behind the link's division, names the supplier.",
  ),
  ediFlag: OPTIONAL_TEXT_INPUT.describe(
    "Y when the vendor takes purchase orders by EDI.",
  ),
  email: OPTIONAL_TEXT_INPUT,
  emailCc: OPTIONAL_TEXT_INPUT,
  creditTermsCode: OPTIONAL_TEXT_INPUT,
  instruction1Text: OPTIONAL_TEXT_INPUT,
  instruction1Code: OPTIONAL_TEXT_INPUT,
  instruction2Text: OPTIONAL_TEXT_INPUT,
  instruction2Code: OPTIONAL_TEXT_INPUT,
  instruction3Text: OPTIONAL_TEXT_INPUT,
  instruction3Code: OPTIONAL_TEXT_INPUT,
  lastColumnLabel: OPTIONAL_TEXT_INPUT.describe(
    "The label token parsed in front of the record's last value, for sources that carry labels. Compared with `expectedLastColumnLabel`.",
  ),
});
export type SupplierSendingSetupInput = z.infer<
  typeof supplierSendingSetupInputSchema
>;

export const upsertSupplierSendingSetupStep = defineStepConfig({
  type: "upsert_supplier_sending_setup",
  name: "Upsert Supplier Sending Setup",
  description:
    "Maintains how each supplier takes purchase orders (EDI or email, addresses, credit terms, printed instructions) from the vendor master, including CDC deletion and ordering.",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: supplierSendingSetupInputSchema,
  outputSchema: z.object({
    written: z.boolean(),
    deleted: z.boolean(),
    skipped: z.boolean(),
    reason: z
      .enum([
        "malformed_row",
        "missing_vendor_code",
        "missing_division",
        "unresolved_supplier",
        "stale_event",
        "not_found",
      ])
      .nullable(),
  }),
  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 500 }),
    writeOriginSystem: z.string().min(1),
    supplierOriginSystem: z
      .string()
      .optional()
      .describe("Supplier lookup origin system."),
    expectedLastColumnLabel: z
      .string()
      .min(1)
      .optional()
      .describe(
        "For label/value files: the label every well-formed record has in front of its last value. A record whose `lastColumnLabel` differs had a delimiter inside a value, so its columns shifted; it is skipped and leaves what is stored alone.",
      ),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
